import {
  AuthToken,
  FormRequest,
  Request,
  RequestMethod,
} from "@campgladiator/cg-common.types.types";
import { buildUrl } from "./fetchUtils";
import { handleGatewayAuthErrors } from "./interceptors";

export type FetchData = {
  authToken?: string;
  body?: string | FormData;
  method: RequestMethod;
  type?: string;
};

export type FetchRequest = {
  authToken?: AuthToken;
  invalidateUserAccessCallback?: (reason: any) => void;
  request: Request | FormRequest;
  updateAuthTokenCallback?: (token: AuthToken) => void;
  type?: "json" | "form";
};

type FetchFormRequest = Omit<FetchRequest, "type">;

export const fetchFormRequest = async ({
  request,
  authToken = null,
  invalidateUserAccessCallback = () => {},
  updateAuthTokenCallback = () => {},
}: FetchFormRequest): Promise<Response> =>
  fetchRequest({
    request,
    authToken,
    invalidateUserAccessCallback,
    updateAuthTokenCallback,
    type: "form",
  });

/** fetchRequest()
 * fetchRequest is the default wrapper function used to handle fetching data from an API.
 * If you are attempting to make an authenticated request with the Keanu API Gateway,
 * you'll need to provide an authToken. For authenticated requests, you can also provide
 * invalidateUserAccessCallback and updateAuthTokenCallback callback functions. These are necessary
 * for the function to automatically regenerate expired Auth Bearer tokens and update application
 * state in the useAppAccess provider.
 */

export const fetchRequest = async ({
  authToken = null,
  request,
  invalidateUserAccessCallback,
  updateAuthTokenCallback,
  type = "json",
}: FetchRequest): Promise<Response> => {
  const {} = request;
  const formFetchData = {
    method: request.requestConfig.method,
    body: request.body,
    authToken: request.requestConfig.authToken,
  };

  const isFormDataRequest = type === "form";

  const jsonFetchData = {
    type: isFormDataRequest ? undefined : "application/json",
    ...formFetchData,
  };

  const fetchData = type === "form" ? formFetchData : jsonFetchData;
  const url = buildUrl(request.requestConfig.url);
  const response = await fetch(url, buildData(fetchData));
  if (!response.ok) {
    const error = response.json();
    throw new Error(await error.then((e) => e.message));
  }
  return await handleGatewayAuthErrors({
    response,
    fetchData,
    authToken,
    invalidateUserAccessCallback,
    updateAuthTokenCallback,
  });
};

export const buildData = ({ method, body, authToken, type }: FetchData) => {
  const safeBody = !!body ? { body } : {};
  const contentType = type ? { "Content-Type": type } : undefined;
  return {
    method,
    headers: {
      ...contentType,
      ...authHeader(authToken),
    },
    ...safeBody,
  };
};

const authHeader = (authToken?: string) =>
  !!authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
